import { makeStyles } from '@material-ui/styles'
import ClusterHostGroupsPicklist from 'app/plugins/openstack/components/infrastructure/clusters/ClusterHostGroupsPicklist'
import clsx from 'clsx'
import { ErrorMessage } from 'core/components/validatedForm/ErrorMessage'
import Text from 'core/elements/Text'
import VirtualMachineFlavorsTable from 'k8s/components/cluster/deployment/form-components/VirtualMachineFlavorsTable'
import FontAwesomeIcon from 'pf9-ui-components/built/components/FontAwesomeIcon'
import QuantitySelector from 'pf9-ui-components/built/components/QuantitySelector'
import DropdownField from 'pf9-ui-components/built/components/validatedForm/DropdownField'
import ToggleSwitchField from 'pf9-ui-components/built/components/validatedForm/ToggleSwitchField'
import AsyncDropdown from 'pf9-ui-components/built/elements/dropdown/AsyncDropdown'
import Theme from 'pf9-ui-components/built/theme-manager/themes/model'
import React, { useEffect, useMemo, useState } from 'react'
import { generateImageOptions, getK8sImages } from '../cluster/deployment/helper'
import { listImages } from 'app/plugins/openstack/components/images/actions'
import { imagesSelector } from 'app/plugins/openstack/components/images/selectors'
import useListAction from 'core/hooks/useListAction'
import { useAppSelector } from 'app/store'
import Alert from 'core/components/Alert'
import SourcePoolFields from '../cluster/deployment/form-components/SourcePoolFields'
import { filterFlavorsBasedOnSourceType } from '../cluster/deployment/form-components/helpers'
import SshKeyPicklist from 'app/plugins/openstack/components/vms/SshKeyPicklist'

interface AddNodeGroupsProps {
  nodePool: any
  vmFlavors: any[]
  storagePools: any[]
  networks: any[]
  onChange: (value: Record<string, any>) => void
  onDeleteNodePool: (id: number) => void
  showDeleteButton?: boolean
  errors?: Errors
  showOpenstackCluster?: boolean
  getParamsUpdater?: (key: string) => (value: any) => void
  params?: Record<string, any>
  k8sVersion?: string
  loadingVmFlavors?: boolean
}

interface Errors {
  [fieldId: string]: string
}

const AddNodeGroups = ({
  nodePool,
  onChange,
  vmFlavors,
  storagePools,
  networks,
  showDeleteButton = false,
  onDeleteNodePool,
  errors = undefined,
  showOpenstackCluster = false,
  getParamsUpdater,
  params,
  k8sVersion,
  loadingVmFlavors = false,
}: AddNodeGroupsProps) => {
  const [collapseCard, setCollapseCard] = useState(false)
  const classes = useStyles()
  const [autoscalingError, setAutoscalingError] = useState('')
  const [error, setError] = useState(null)

  // Images
  const { loading: loadingImages } = useListAction(listImages, {
    params,
  })
  const images = useAppSelector(imagesSelector)
  const imageOptions = useMemo(() => {
    setError(null)
    const k8sImages = getK8sImages({
      k8sVersion,
      images,
      setError,
    })
    return generateImageOptions(k8sImages)
  }, [images, k8sVersion])

  const filteredVmFlavors = useMemo(() => {
    if (!params?.sourceType) {
      return vmFlavors || []
    }
    return filterFlavorsBasedOnSourceType(vmFlavors, params.sourceType)
  }, [vmFlavors, params?.sourceType])

  const storageOptions = useMemo(
    () =>
      storagePools
        .filter((storagePool) => !!storagePool.name)
        .map((storagePool) => ({
          label: storagePool.name,
          value: storagePool.name,
        })),
    [storagePools],
  )

  const networkOptions = useMemo(
    () =>
      networks?.map((network) => ({
        label: network?.name,
        value: network?.id,
      })),
    [networks],
  )

  const subnetOptionsByNetworkId = useMemo(
    () =>
      networks.reduce((acc, network) => {
        acc[network?.id] = network?.subnetDetails?.map((subnet) => ({
          label: `${subnet?.name}: ${subnet?.cidr}`,
          value: subnet?.id,
        }))
        return acc
      }, {} as Record<string, { label: string; value: string }[]>),
    [networks],
  )

  const handleVmChange = (value) => {
    onChange(value)
  }

  const handleAutoscaling = (enableAutoscaling) => {
    onChange(
      enableAutoscaling
        ? { autoscaling: { max: nodePool?.replicas || 1 } }
        : { autoscaling: undefined },
    )
  }

  const handleMaxNodesChange = (value) => {
    onChange({ autoscaling: { max: value } })
  }

  useEffect(() => {
    if (nodePool.autoscaling?.max < nodePool?.replicas) {
      setAutoscalingError(
        'The value must be greater than or equal to the selected amount of instance types.',
      )
    } else {
      setAutoscalingError('')
    }
  }, [nodePool.autoscaling?.max, nodePool.replicas])

  return (
    <div className={classes.content}>
      {/* Select Availability Zone */}
      {showOpenstackCluster && (
        <>
          {error ? (
            <Alert
              variant="error"
              title="No Images Available"
              message={
                <Text variant="body2">{`No images available for kubernetes version of the selected cluster - ${params?.k8sVersion}.`}</Text>
              }
            />
          ) : null}
          <div className={classes.dropdownFields}>
            <DropdownField
              id="openstackCluster"
              label="Virtualized Cluster"
              DropdownComponent={ClusterHostGroupsPicklist}
              value={params?.openstackCluster}
              onChange={getParamsUpdater('openstackCluster')}
              fullWidth
              info="Select the virtualized cluster to which the kubernetes cluster will be attached."
              disableClusterWithoutHosts
              required
            />
            <DropdownField
              id="sshKey"
              label="SSH Key"
              includePublicKey
              DropdownComponent={SshKeyPicklist as React.FC<any>}
              value={params?.sshKey}
              onChange={getParamsUpdater('sshKey')}
              fullWidth
              info="Select an SSH key for your VMs."
              offset={{ vertical: 0, horizontal: -75 }}
              required
              hideNoneOption
            />
            <DropdownField
              id="imageName"
              label="Available Images"
              placeholder={imageOptions?.length === 0 ? 'No Images Available' : 'Select an Image'}
              DropdownComponent={AsyncDropdown}
              loading={loadingImages}
              items={imageOptions}
              value={params?.imageName}
              onChange={getParamsUpdater('imageName')}
              selectFirst
              required
            />
            <SourcePoolFields params={params} getParamsUpdater={getParamsUpdater} />
          </div>
        </>
      )}

      <div className={clsx(classes.vmSelection, classes.borderBottom)}>
        <VirtualMachineFlavorsTable
          data={filteredVmFlavors}
          selectedVmFlavor={nodePool?.flavor}
          selectedVmQuantity={nodePool?.replicas}
          onChange={handleVmChange}
          data-testid="vm-flavors-table"
          loadingVmFlavors={loadingVmFlavors}
        />
        {errors && errors.flavor && (
          <ErrorMessage data-testid="flavor-error">{errors.flavor}</ErrorMessage>
        )}
      </div>
      <div
        className={clsx(classes.autoscalingFields, classes.borderBottom)}
        data-testid="autoscaling-fields"
      >
        <ToggleSwitchField
          id={`autoscaling-${nodePool.id}`}
          label="Automatically scale this Node Group"
          onChange={handleAutoscaling}
          data-testid="autoscaling-toggle"
        />
        {nodePool.autoscaling !== undefined && (
          <div className={classes.quantitySelector} data-testid="quantity-selector">
            <Text variant="body2" data-testid="max-nodes-label">
              Max Nodes
            </Text>
            <QuantitySelector
              id={`maxNodes-${nodePool.id}`}
              value={nodePool.autoscaling.max || 0}
              onChange={handleMaxNodesChange}
              min={nodePool.replicas}
              data-testid="max-nodes-input"
            />
            {autoscalingError && (
              <ErrorMessage data-testid="autoscaling-error">{autoscalingError}</ErrorMessage>
            )}
            {errors && errors.autoscaling && (
              <ErrorMessage data-testid="autoscaling-error">{errors.autoscaling}</ErrorMessage>
            )}
          </div>
        )}
      </div>
      {/* To be uncommented once we have implementation from backend in later release */}

      {/* <div className={clsx(classes.dropdownFields)} data-testid="dropdown-fields">
        <div style={{ position: 'relative' }} data-testid="storage-dropdown">
          <DropdownField
            id={`storage-${nodePool.id}`}
            label="Storage Pool"
            DropdownComponent={AsyncDropdown}
            items={storageOptions}
            value={nodePool.storage}
            onChange={(value) => onChange({ storage: value })}
            // required
            disabled
            tooltip="Cluster node VMs will get deployed using hypervisor local storage. Support for using block storage for cluster node VMs is coming soon"
            data-testid="storage-dropdown"
          />
          <Text
            variant="caption1"
            className={classes.storageComingSoonFlag}
            data-testid="storage-coming-soon"
          >
            Coming soon!
          </Text>
        </div>
      </div> */}
      {/* Networks */}
      <div
        className={clsx(
          classes.dropdownFields,
          classes.networkDropdowns,
          showDeleteButton && classes.networkDropdownsWithDelete,
          classes.borderBottom,
        )}
        data-testid="network-dropdowns"
      >
        <DropdownField
          id={`network-${nodePool.id}`}
          label="Network"
          DropdownComponent={AsyncDropdown}
          items={networkOptions}
          value={nodePool.network}
          onChange={(value) => {
            onChange({ network: value, subnet: '' })
          }}
          required
          data-testid="network-dropdown"
        />
        <DropdownField
          id={`subnet-${nodePool.id}`}
          label="Subnet"
          DropdownComponent={AsyncDropdown}
          items={subnetOptionsByNetworkId[nodePool?.network] || []}
          value={nodePool.subnet}
          onChange={(value) => {
            onChange({ subnet: value })
          }}
          disabled={!nodePool?.network}
          required
          data-testid="subnet-dropdown"
        />
      </div>
      {showDeleteButton && (
        <div className={classes.deleteNodePoolButton} data-testid="delete-node-pool-button">
          <FontAwesomeIcon
            className={classes.minusIcon}
            onClick={() => onDeleteNodePool(nodePool?.id)}
            size="xl"
            data-testid="delete-icon"
          >
            minus-circle
          </FontAwesomeIcon>
          <Text variant="body2" data-testid="delete-label">
            Delete Node Group
          </Text>
        </div>
      )}
    </div>
  )
}

export default AddNodeGroups

const useStyles = makeStyles<Theme>((theme) => ({
  content: {
    display: 'grid',
    gridAutoFlow: 'row',
    gridGap: theme.spacing(4),
  },
  vmSelection: {
    display: 'grid',
    gridGap: theme.spacing(2),
  },
  borderBottom: {
    paddingBottom: theme.spacing(3),
    borderBottom: `1px solid ${theme.components.card.border}`,
  },
  autoscalingFields: {
    display: 'grid',
    gridGap: theme.spacing(2),
  },
  quantitySelector: {
    display: 'grid',
    gridTemplateColumns: 'max-content',
    gridGap: theme.spacing(2),
    marginTop: theme.spacing(),
  },
  dropdownFields: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gridGap: theme.spacing(3),
    alignItems: 'baseline',
  },
  networkDropdowns: {
    paddingBottom: theme.spacing(2),
  },
  networkDropdownsWithDelete: {
    paddingBottom: theme.spacing(4),
  },
  deleteNodePoolButton: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, max-content)',
    padding: theme.spacing(1, 0, 3, 0),
    alignItems: 'center',
  },
  minusIcon: {
    color: theme.palette.blue[700],
    marginRight: theme.spacing(1),
    fontWeight: 900,
  },
  storageComingSoonFlag: {
    position: 'absolute',
    top: 32,
    right: 0,
    backgroundColor: theme.palette.blue['700'],
    color: theme.palette.common.white,
    padding: theme.spacing(1, 2, 1, 3),
    whiteSpace: 'nowrap',
    zIndex: 10,
    display: 'inline-block',
    clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0 100%, 10px 50%)', // Creates the transparent triangle on the left
  },
}))
